/* ================================================================ */
// DATA SELECTORS | index.js
/* ================================================================ */
/* ========================================== */
// Imports
/* ========================================== */
import createCachedSelector from 're-reselect';
import {createSelector} from 'reselect';
import PropTypes from 'prop-types';
//import isEqual from 'lodash.isEqual';
// Theme Setup
import {getTheme} from '@myglobe/core/dist/styles/theme';
import createMuiTheme from '@material-ui/core/styles/createMuiTheme';
// Types
import {VisEngineTypes, PageTypes} from '@myglobe/core/dist/types';
import { isNullOrUndefined } from 'util';
/* ========================================== */
// LOADING
/* ========================================== */
/* ========================================== */
/**
 * Get Loading Status
 * ------------------
 * Determines current loading state either
 * by all keys within in the loading state
 * or according to a given specific loading key
 *
 * @param {object} store reducer store
 * @param {string} checkState loading key to check
 */
export const getLoadingStatusV2 = (checkState) => {
    return createSelector(
        store => store.Data.loading,
        (loading) => {
            return (checkState && !isNullOrUndefined(loading[checkState])) ? loading[checkState] : Object.values(loading).includes(true);
        }
    );
};
export const getLoadingStatus = (store, checkState) => (checkState && store.Data.loading[checkState] !== null) ? store.Data.loading[checkState] : Object.values(store.Data.loading).includes(true);
export const getLoadingStatusSelector = statuses => {
    return createSelector(
        store=>store.Data.loading,
        (loading)=>{
            const loadingKeys = Object.keys(loading);
            const loadingValues = Object.values(loading);
            // Check all statuses exist
            if(statuses.every(status=>loadingKeys.includes(status))) {
                // Get combined loading state of all requested statuses
                // > Loading state is true if one status is found to be loading
                return statuses.some(status=>loading[status] === true);
            } else {
                // Universal loading (all loading statues)
                return loadingValues.includes(true);
            }
        }
    );
};
getLoadingStatusSelector.propTypes = {
    statuses: PropTypes.arrayOf(PropTypes.string).isRequired
};
/* ========================================== */
// GENERIC
/* ========================================== */
/* ========================================== */
/**
 * Find Missing Ids
 * ----------------
 * Checks for existing ids within
 * a given dataset and returns an array of missing ids
 *
 * @param {object} dataset reducer state variable
 * @param {array} ids ids to check
 *
 * @return {array} array of missing ids
 */
export const getMissingIds = (datasetName, incomingIds) => {
    return createSelector(
        store=>store.Data[datasetName],
        //getLoadingStatusSelector([datasetName]),
        (dataset, /*isLoading*/) => {
            if(/*!isLoading && */dataset) {
                const datasetIds = dataset.byId;
                const missingIds = incomingIds.filter(incomingId=>!datasetIds.includes(incomingId));
                console.debug('Data (Selector) | ', 'getMissingIDs -', 'datasetIds:', datasetIds, 'incomingIds:', incomingIds, 'missingIds:', missingIds);
                return missingIds;
            } else return [];
        }
    );
};
getMissingIds.propTypes = {
    datasetName: PropTypes.oneOf(['globe', 'pages', 'stories', 'panels', 'states', 'layers']).isRequired,
    incomingIds: PropTypes.arrayOf(PropTypes.string).isRequired
};
/* ========================================== */
/**
 * Find Missing Ids
 * ----------------
 * Checks for existing ids within
 * a given dataset and returns an array of related missing uuids
 *
 * @param {string} datasetName reducer state variable name in
 *  generic dataset storage format
 * @param {object} incomingIds ids to search in map format of
 * `dataset index to database uuid {<index>: uuid}
 * @return {array} array of missing uuids according to identified missing indexes
 */
export const getMissingUUIDs = (datasetName, incomingIds) => {
    return createSelector(
        store=>store.Data[datasetName],
        //getLoadingStatusSelector([datasetName]),
        (dataset, /*isLoading*/) => {
            if(/*!isLoading && */dataset) {
                // Dataset Ids (will be array if initialised, otherwise uninitialised state empty array fallback will trigger all missing values)
                const datasetIds = dataset.byId || [];
                const missingIds = Object.keys(incomingIds).filter(incomingId=>!datasetIds.includes(incomingId));
                const missingUUIDs = missingIds.map(id=>incomingIds[id]);
                console.debug('Data (Selector) | ', 'getMissingUUIDs -', 'datasetIds:', datasetIds, 'incomingIds:', incomingIds, 'missingIds:', missingIds, 'missingUUIDs:', missingUUIDs);
                return missingUUIDs;
            } else return [];
        }
    );
};
getMissingUUIDs.propTypes = {
    datasetName: PropTypes.oneOf(['globe', 'pages', 'stories', 'panels', 'states', 'layers']).isRequired,
    incomingIds: PropTypes.object.isRequired//arrayOf(PropTypes.string).isRequired
};
/* ========================================== */
// GLOBE
/* ========================================== */
export const getGlobe = store => store.Data.globe;
export const getGlobeName = store => store.Data.globe.name;
export const getGlobeTitle = store => store.Data.globe.title;
export const getGlobeConfig = store => store.Data.globe.config;
export const getGlobeTheme = createSelector(
    getGlobeConfig,
    (globeConfig) => {
        const theme = globeConfig.theme || {};
        const type = (theme.type) ? theme.type : 'dark';
        const baseTheme = getTheme(type);
        const themeConfig = {...baseTheme, ...theme};
        return createMuiTheme(themeConfig);
    }
);
export const getError = store => store.Data.error;
export const getDefaultState = store => store.Data.globe.defaultState;
/* ========================================== */
// DATASETS
/* ========================================== */
export const getDatasetsById = store => store.Data.datasets.byId;
export const getDatasetsByHash = store => store.Data.datasets.byHash;
export const getDataset = (id) => {
    return createSelector(
        getDatasetsByHash,
        (datasetHash) => {
            return datasetHash[id] || null;
        }
    );
};
/* ========================================== */
// LAYERS
/* ========================================== */
export const getLayersById = store => store.Data.layers.byId;
export const getLayersByHash = store => store.Data.layers.byHash;
export const getLayersIdsByType = type => {
    return createSelector(
        getLayersById,
        getLayersByHash,
        (layerIds, layerHash) => {
            return layerIds.filter(layerId=>layerHash[layerId].type.toLowerCase() === type);
        }
    );
};
getLayersIdsByType.propTypes = {
    type: PropTypes.oneOf(Object.values(VisEngineTypes))
};
export const getLayersHashByType = type => {
    return createCachedSelector(
        getLayersById,
        getLayersByHash,
        (layerIds, layerHash) => {
            let layers = [];
            layerIds.forEach(layerId=>{
                if(layerHash[layerId].type.toLowerCase() === type)
                    layers.push(layerHash[layerId]);
            });
            return layers;
        }
    )(
        (store, type)=>type
    );
};
getLayersHashByType.propTypes = {
    type: PropTypes.oneOf(Object.values(VisEngineTypes))
};
export const getFilteredLayers = layers => {
    return createSelector(
        getLayersById,
        getLayersByHash,
        (layerIds, layerHash)=>{
            const filteredLayers = layerIds.filter(layerId => layers.includes(layerId)).map(layerId=>layerHash[layerId]);
            return filteredLayers;
        }
    );
};
/* ========================================== */
/**
 * Get Layer Id
 * ------------
 * Get layer id from existing list of ids
 * @param {string} id layer id to check
 */
export const getLayerId = (store, id) => store.Data.layers.byId.includes(id) ? id : null;
/* ========================================== */
/**
 * Get Layer
 * ---------
 * Retrieves layer has from a valid layer ID
 * @param {string} layerId layer id
 */
export const getLayer = createCachedSelector(
    getLayerId,
    getLayersByHash,
    getLoadingStatusSelector(['layers']),
    (layerId, layerHash, isLoading) => {
        console.debug('Data (Selector) | ', 'getLayer -', 'layerId:', layerId, 'layerHash:', layerHash, 'isLoading:', isLoading);
        // Return corresponding layer hash according to existing layer id and loading status
        return (!isLoading && layerId) ? layerHash[layerId] : null;
    }
)(
    (_store_, layerId)=>layerId
);
getLayer.propTypes = {
    layerId: PropTypes.string.isRequired
};
/* ========================================== */
/**
 * Get Deck Layer
 * --------------
 * Constructs a single layer json configuration based on
 * the provided layer id
 *
 * @param {string} layerId
 */
export const getDeckLayer = (layerId) => {
    return createSelector(
        getLayersHashByType('deckgl'),
        getDatasetsByHash,
        (deckLayersHash, datasetsHash) => {
            // Get Deck layer from deck layer only hash
            const deckLayer = deckLayersHash[layerId];
            if(deckLayer) {
                // Get json config and convert functions from string to function
                let config = deckLayer.config;
                const datasetId = deckLayer.dataset;
                // Get dataset endpoint data or endpoint fallback
                const data = (datasetsHash[datasetId]) ? datasetsHash[datasetId].endpointData || datasetsHash[datasetId].endpoint : null;
                config.data = data;
                return config;
            } else {
                return null;
            }
        }
    );
};
getDeckLayer.propTypes = {
    layerId: PropTypes.string.isRequired,
    datasetId: PropTypes.string,
};
export const getDeckLayers = (layers) => {
    return createSelector(
        layers.map(layerId=>getDeckLayer(layerId)),
        (deckLayers)=>deckLayers
    );
};
/*
export const getDeckConfig = (layers) => {
    return createSelector(
        layers.map(layer=>getDeckLayer(layer.id, layer.datasetId);
    )
}
*/
/* ========================================== */
// STATES
/* ========================================== */
export const getStatesById = store => store.Data.states.byId;
export const getStatesByHash = store => store.Data.states.byHash;
export const getStatesFlatMap = createSelector(
    getStatesByHash,
    (statesHash) => {
        const flatMap = Object.values(statesHash).flatMap(x=>x);
        return flatMap;
    }
);
/* ========================================== */
/**
 * Get State Id
 * ------------
 * Get state id from existing list of ids
 * @param {string} id state id to check
 */
export const getStateId = (store, id) => store.Data.states.byId.includes(id) ? id : null;
/* ========================================== */
/**
 * Get State
 * ---------
 * Retrieves a state from Data reducer
 * according to a given id
 *
 * @param {string} stateId id of state
 *
 * @returns {object} state object or null
 */
export const getState = createCachedSelector(
    getStateId,
    getStatesByHash,
    getLoadingStatusSelector(['states']),
    (stateId, stateHash, isLoading) => {
        console.debug('Data (Selector) | ', 'getState -', 'stateId:', stateId, 'stateHash:', stateHash, 'isLoading:', isLoading);
        // Return corresponding state hash according to existing state id and loading status
        return (!isLoading && stateId) ? stateHash[stateId] : null;
    }
)(
    (_store_, stateId)=>{console.log(stateId); return stateId;}
);
/*export const getState = stateId => {
    return createSelector(
        getStatesById,
        getStatesByHash,
        getLoadingStatusSelector(['states']),
        (stateIds, stateHash, isLoading) => {
            if(isLoading) {
                return null;
            } else if(stateIds.includes(stateId)) {
                return stateHash[stateId];
            } else {
                return null;
            }
        }
    );
};*/
getState.propTypes = {
    stateId: PropTypes.string.isRequired
};
/* ========================================== */
/**
 * Get Active States
 * -----------------
 * Retrieves the current active states deduced from
 * active panels of a given story ID (slug)
 *
 * @param {string} storyID story slug
 */
export const getActiveStates = createCachedSelector(
    (store, storyID)=>getActivePanels(store, storyID),
    getStatesById,
    getStatesByHash,
    getLoadingStatusSelector(['panels', 'states']),
    (panels, stateIds, stateHash, isLoading) => {
        if(isLoading) {
            return [];
        } else if(panels) {
            const states = panels.filter(panel=>stateIds.includes(panel.state)).map(panel=>stateHash[panel.state]);
            console.debug('getActiveStates', states);
            return states;
        } else {
            return [];
        }
    }
)(
    (_store_, storyId)=>`${storyId}_states`
);
getActiveStates.propTypes = {
    storyID: PropTypes.string.isRequired
};
/* ========================================== */
// PAGES
/* ========================================== */
export const getPagesById = store => store.Data.pages.byId;
export const getPagesByHash = store => store.Data.pages.byHash;
export const getPageId = (store, id) => store.Data.pages.byId.includes(id) ? id : null;
export const getPage = createCachedSelector(
    getPageId,
    getPagesByHash,
    getLoadingStatusSelector(['pages']),
    (pageId, pageHash, isLoading) => {
        console.debug('Data (Selector) | ', 'getPage -', 'pageId:', pageId, 'pageHash:', pageHash, 'isLoading:', isLoading);
        // Return corresponding page hash to pageId if loaded and id present
        return (!isLoading && pageId) ? pageHash[pageId] : null;
    }
)(
    (_store_, pageId)=>pageId
);
getPage.propTypes = {
    pageId: PropTypes.string.isRequired
};
export const getPageTypes = createSelector(
    getPagesById,
    getPagesByHash,
    (pageIds, pageHash) => {
        const pageTypes = [...new Set(pageIds.map(pageId=>pageHash[pageId].type.toLowerCase()))];
        console.debug('Data (Selector) | ', 'getPageTypes -', 'pageTypes:', pageTypes);
        return pageTypes;
    }
);
/*
export const getPageType = createCachedSelector(
    (store, pageId) => getPage(store, pageId),
    (page) => {
        console.debug('Data (Selector) | ', 'getPageType -', 'page:', page);
        // Return corresponding page hash to pageId if loaded and id present
        return (page) ? page.type : PageTypes.STATIC;
    }
)(
    (_store_, pageId)=>pageId
);
getPageType.propTypes = {
    pageId: PropTypes.string.isRequired
};
*/
/* ========================================== */
// MENU
/* ========================================== */
/**
 * Get Menu Items
 * --------------
 * Populates dynamic array of menu items
 * according to menuIndex and id for index specific selector
 *
 * @param {string} dataIndex data selector index
 * @param {string} slug slug for data selector
 */
/*
export const getMenuItems = (dataIndex, slug) => {
    return createSelector(
        (store) => {console.log(dataIndex, slug); return dataIndex === 'panels' ? getActivePanels(store, slug) : [];},
        getLoadingStatusSelector([dataIndex]),
        (data, isLoading)=>{
            console.debug('Data (Selector) | ', 'getMenuItems -', 'data:', data, 'isLoading:', isLoading);
            const items = data.map(dataItem => {
                return {
                    title: dataItem.title,
                    icon: 'trip_origin',
                    slug: dataItem.slug,
                    path: `${dataItem.slug}/${dataItem.slug}`
                };
            });
            // Return corresponding menu items to menuIndex and if loaded and id present
            return (!isLoading && data) ? items : null;
        }
    );
};
getMenuItems.propTypes = {
    dataIndex: PropTypes.string.isRequired,
    slug: PropTypes.string
};
/*
export const getMenuItems = createCachedSelector(
    // TODO: improve menu index selection function via helper
    //(menuIndex, id)=>[menuIndex, id, props],
    //(store, dataIndex, slug) => {return dataIndex === 'panels' ? getActivePanels(store, slug) : null},
    (_store_, slug)=>getActivePanels(_store_, slug),
    getLoadingStatusSelector(['panels', 'stories']),
    (data, isLoading) => {
        console.debug('Data (Selector) | ', 'getMenuItems -', 'data:', data, 'isLoading:', isLoading);
        if(isLoading)
            return [];
        else {
            const items = data.map(dataItem => {
                return {
                    title: dataItem.title,
                    slug: dataItem.slug
                };
            });
            return items;
        }
        // Return corresponding menu items to menuIndex and if loaded and id present
    }
)(
    (_store_, dataIndex, slug)=>`${dataIndex}:${slug}`
);*/
/* ========================================== */
// STORIES
/* ========================================== */
export const getStoriesById = store => store.Data.stories.byId;
export const getStoriesByHash = store => store.Data.stories.byHash;
export const getStoriesFlatMap = createSelector(
    getStoriesByHash,
    (storiesHash) => {
        const flatMap = Object.values(storiesHash).flatMap(x=>x);
        return flatMap;
    }
);
export const getStoryId = (store, id) => store.Data.stories.byId.includes(id) ? id : null;
/* ========================================== */
/**
 * Get Story
 * ---------
 * Returns a stored story object from state
 * according to the specified storyId or a null
 * if it doesn't exist or the data is loading
 *
 * @param {string} storyId story id
 */
export const getStory = createCachedSelector(
    getStoryId,
    getStoriesByHash,
    getLoadingStatusSelector(['stories']),
    (storyId, storyHash, isLoading) => {
        console.debug('Data (Selector) | ', 'getStory -', 'storyId:', storyId, 'storyHash:', storyHash, 'isLoading:', isLoading);
        // Return corresponding story hash to storyId if loaded and id present
        return (!isLoading && storyId) ? storyHash[storyId] : null;
    }
)(
    (_store_, storyId)=>storyId
);
getStory.propTypes = {
    storyId: PropTypes.string.isRequired
};
/* ========================================== */
// PANELS
/* ========================================== */
export const getPanelsById = store => store.Data.panels.byId;
export const getPanelsByHash = store => store.Data.panels.byHash;
/* ========================================== */
/**
 * Get Panel By Slug
 * -----------------
 * Finds a panel by a given slug
 * string and returns the panel object
 * or null
 *
 * @param {string} slug panel slug to search for
 * @returns {object || null} panel object
 */
export const getPanelBySlug = slug => {
    return createSelector(
        getPanelsById,
        getPanelsByHash,
        getLoadingStatusSelector(['panels']),
        (panelIds, panelHash, isLoading) => {
            if(isLoading) {
                return null;
            }
            const panelId = panelIds.some(id=>id.includes(slug));
            return panelHash[panelId] || null;
        }
    );
};
getPanelBySlug.propTypes = {
    slug: PropTypes.string.isRequired
};
/* ========================================== */
/**
 * Get Active Panels
 * -----------------
 * Retrieves loaded panels for the given
 * story
 *
 * @param {object} storySlug story slug string
 * @returns {array} active story panels
 */
export const getActivePanels = createCachedSelector(
    (store, storySlug)=>{return storySlug?getStory(store, storySlug):null;},
    getPanelsById,
    getPanelsByHash,
    getLoadingStatusSelector(['stories', 'panels']),
    (story, panelIds, panelsHash, isLoading) => {
        console.debug('Data (Selector) | ', 'getActivePanels (Params) -', 'story:', story, 'panelIds:', panelIds, 'panelsHash:', panelsHash, 'isLoading:', isLoading);
        if(isLoading) {
            return [];
        } else if(story) {
            // Filter, sort, and output existing panels
            const storyPanels = story.panels.filter(panelId=>panelIds.includes(panelId))
                .sort((a,b)=>panelsHash[a].position > panelsHash[b].position ? 1 : -1)
                .map(panelId=>panelsHash[panelId]);
            console.debug('Data (Selector) | ', 'getActivePanels (Return) -', 'storyPanels:', storyPanels);
            return storyPanels;
        } else {
            return [];
        }
    }
)(
    (_store_, storySlug)=>`${storySlug}_panels`
);
getActivePanels.propTypes = {
    storySlug: PropTypes.string.isRequired
};